import type { Table } from 'apache-arrow'
import type { SampleBucket } from '../../SampleGrouping/OperationTypes.ts'
import type { SinkTable, SinkTableColumn, SinkTableRow } from './SinkTable.ts'

export const CUBE_RANKS = ['domain', 'kingdom', 'phylum', 'class', 'order', 'family', 'genus', 'species'] as const

/**
 * The cube behind a source, one row per (group, sample, taxon path), with none of the source's knobs
 * applied: every rank is its own column and grade is kept per row, so the zoom path, grade threshold,
 * taxon level and group selection can all be re-applied to the file afterwards. Ranks stay positional
 * (an empty cell is a missing rank) — compacting them would shift a rank-skipping ladder into the
 * wrong columns.
 *
 * A sample in no group is still written, with an empty group, so the export covers the whole cube.
 */
export function buildSourceTable(opts: {
  table: Table
  buckets: SampleBucket[]
  selectedBucketKeys: Set<string>
}): SinkTable {
  const { table, buckets, selectedBucketKeys } = opts

  const memberships = new Map<string, SampleBucket[]>()
  const sampleNames = new Map<string, string>()
  for (const bucket of buckets) {
    for (const sample of bucket.samples) {
      const list = memberships.get(sample.id)
      if (list) list.push(bucket)
      else memberships.set(sample.id, [bucket])
      const name = sample.sampleMetadata?.customerSampleName ?? sample.customerSampleName
      if (name) sampleNames.set(sample.id, name)
    }
  }

  const sampleIdCol = table.getChild('sampleId')
  const taxaCol = table.getChild('taxa')
  const readsCol = table.getChild('reads')
  const gradeCol = table.getChild('grade')
  const sparkTopCol = table.getChild('sparkTop')
  const sparkAltCol = table.getChild('sparkAlt')
  const spinIdCol = table.getChild('spinId')

  const rows: SinkTableRow[] = []
  let hasSpin = false
  for (let i = 0; i < table.numRows; i++) {
    const sampleId = (sampleIdCol?.get(i) as string | null) ?? ''
    const taxaRow = taxaCol?.get(i) as { get(j: number): string | null } | null
    const spinId = (spinIdCol?.get(i) as string | null) ?? null
    if (spinId) hasSpin = true

    const base: SinkTableRow = {
      sampleId,
      sampleName: sampleNames.get(sampleId) ?? '',
      reads: Number(readsCol?.get(i) ?? 0),
      grade: (gradeCol?.get(i) as number | null) ?? null,
      sparkTop: (sparkTopCol?.get(i) as number | null) ?? null,
      sparkAlt: (sparkAltCol?.get(i) as number | null) ?? null,
      spinId
    }
    CUBE_RANKS.forEach((rank, j) => {
      base[rank] = taxaRow?.get(j) || null
    })

    const groups = memberships.get(sampleId)
    if (!groups) {
      rows.push({ ...base, group: '', groupSelected: '' })
      continue
    }
    for (const bucket of groups) {
      rows.push({ ...base, group: bucket.label, groupSelected: selectedBucketKeys.has(bucket.key) ? 'yes' : 'no' })
    }
  }

  const columns: SinkTableColumn[] = [
    { key: 'group', label: 'group' },
    { key: 'groupSelected', label: 'groupSelected' },
    { key: 'sampleId', label: 'sampleId' },
    { key: 'sampleName', label: 'sampleName' },
    ...CUBE_RANKS.map((rank) => ({ key: rank, label: rank })),
    { key: 'reads', label: 'reads', numeric: true },
    { key: 'grade', label: 'grade', numeric: true },
    { key: 'sparkTop', label: 'sparkTop', numeric: true },
    { key: 'sparkAlt', label: 'sparkAlt', numeric: true },
    ...(hasSpin ? [{ key: 'spinId', label: 'spinId' }] : [])
  ]

  return { columns, rows }
}
